'use client';

import { useEffect, useMemo, useRef } from 'react';

import { CovChart, CovStocks, Player } from '@asa_xbt/binary-chart';
// @ts-ignore
import { CovChartType } from '@asa_xbt/binary-chart/lib/cov/CovData';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs/Tabs';
import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';

import { PublicStrategy } from '@/api/copy-trading/types';
import {
  ChartType,
  INTERVALS,
  Interval,
  useStrategyChartData,
} from '@/api/copy-trading/useStrategyChartData';
import { Select } from '@/blocks/components/Controls/Select';
import tabsStyles from '@/blocks/components/Controls/Tabs/styles.module.scss';

import { Card } from '../../../../../Components/Card';
import { getChartConfig } from '../shared/getChartConfig';
import { getChartScale } from '../shared/getChartScale';
import styles from './styles.module.scss';

export function ChartsGroup({ strategy }: { strategy: PublicStrategy }) {
  const locale = useLocale();
  const config = getChartConfig(locale);
  const refChartContainer = useRef<null | HTMLDivElement>(null);
  const refStocks = useRef<CovStocks>(new CovStocks());
  const refChart = useRef<CovChart>(new CovChart(refStocks.current, config));
  const refPlayer = useRef<undefined | Player>();
  const tr = useTranslations();

  const { setChartType, chartType, interval, setInterval, setChartMounted } = useStrategyChartData({
    strategyId: strategy.strategyId,
    refChartContainer,
    onChange(data) {
      refStocks.current.update({
        data: data,
        currency: strategy.currency,
        type: chartType as CovChartType,
        barSize: INTERVALS[(data.timeFrame || interval) as Interval].barSize,
        scale: getChartScale(strategy.currency),
      });
    },
  });

  function mountChart() {
    if (typeof refChartContainer.current && refChartContainer.current?.childElementCount === 0) {
      refPlayer.current = new Player(refChartContainer.current);
      refPlayer.current?.start(refChart.current);
      setChartMounted(true);
    }
  }

  useEffect(mountChart, [setChartMounted]);

  const MemoizedTabs = useMemo(
    () => (
      <Tabs
        value={chartType}
        className={classNames(tabsStyles.outlinedTabs, styles.tabs)}
        variant={'scrollable'}
        scrollButtons={false}
        onChange={(e, chartT) => setChartType(chartT)}
      >
        <Tab
          disableRipple
          value={ChartType.YIELD}
          label={tr.rich(
            'pages.strategyPage.sections.main.widgets.main.tabs.tradingData.widgets.roi.tabs.profit.label',
          )}
        />
        <Tab
          disableRipple
          value={ChartType.MARGIN}
          label={tr.rich(
            'pages.strategyPage.sections.main.widgets.main.tabs.tradingData.widgets.roi.tabs.margin.label',
          )}
        />
        <Tab
          disableRipple
          value={ChartType.EQUITY}
          label={tr.rich(
            'pages.strategyPage.sections.main.widgets.main.tabs.tradingData.widgets.roi.tabs.providerEquity.label',
          )}
        />
      </Tabs>
    ),
    [chartType, setChartType, tr],
  );

  const MemoizedSelect = useMemo(
    () => (
      <Select
        value={interval}
        onChange={(e) => {
          setInterval(e.target.value as Interval);
        }}
        values={[
          { label: tr('pages.strategyPage.sections.main.periodSelect.labels.day'), value: Interval.DAY },
          { label: tr('pages.strategyPage.sections.main.periodSelect.labels.week'), value: Interval.WEEK },
          { label: tr('pages.strategyPage.sections.main.periodSelect.labels.month'), value: Interval.MONTH },
          { label: tr('pages.strategyPage.sections.main.periodSelect.labels.all'), value: Interval.ALL },
        ]}
      />
    ),
    [interval, setInterval],
  );

  return (
    <Card
      title={tr.rich('pages.strategyPage.sections.main.widgets.main.tabs.tradingData.widgets.roi.title')}
      padding={'small'}
      innerContentMaxWidthDesktop={670}
      className={styles.chartsGroup}
    >
      <div className={styles.controls}>
        {MemoizedTabs}
        {MemoizedSelect}
      </div>
      <div className={styles.chartContainer}>
        <div ref={refChartContainer}></div>
      </div>
    </Card>
  );
}
